import { useQuery } from '@tanstack/react-query';

import { OptLogVolumeActionsCountResponse } from '~/shared/types/api';
import { useXFetch } from '~/shared/utils/useXFetch';
import { DEFAULT_REFETCH_INTERVAL_MS } from '~/shared/constants/polling';
import {
  ActionTypeFilterValue,
  actionTypeValues,
  optLogActionsCountUrl,
} from '~/shared/components/filters/actionTypeFilter';

export interface UseActionTypeFilterCountsParams {
  baseCountQueryString: string;
  enabled?: boolean;
}

export function useActionTypeFilterCounts({
  baseCountQueryString,
  enabled = true,
}: UseActionTypeFilterCountsParams): {
  typeCounts: Partial<Record<ActionTypeFilterValue, number>>;
  isLoading: boolean;
} {
  const api = useXFetch();

  const query = useQuery({
    queryKey: ['optLog', 'action-type-filter-counts', baseCountQueryString],
    enabled: enabled && !!baseCountQueryString,
    refetchOnWindowFocus: false,
    refetchInterval: DEFAULT_REFETCH_INTERVAL_MS,
    queryFn: async () => {
      const results = await Promise.allSettled(
        actionTypeValues.map(async (typeValue) => {
          const response = await api<OptLogVolumeActionsCountResponse>(
            optLogActionsCountUrl(baseCountQueryString, typeValue)
          );
          return [typeValue, response?.count ?? 0] as const;
        })
      );

      const typeCounts: Partial<Record<ActionTypeFilterValue, number>> = {};
      for (const result of results) {
        if (result.status === 'fulfilled') {
          const [typeValue, count] = result.value;
          typeCounts[typeValue] = count;
        }
      }
      return typeCounts;
    },
  });

  return {
    typeCounts: query.data ?? {},
    isLoading: query.isLoading && !query.data,
  };
}
